"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { PackageCheck, Scale, MapPin, Warehouse, ArrowUpRight, PhoneCall } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const factors = [{
  step: "01",
  title: "Equipment Being Shipped",
  summary: "Confirm whether the order includes only the bull or a larger package with the controller, inflatable, blower and accessories.",
  icon: PackageCheck,
  tag: "Package scope"
}, {
  step: "02",
  title: "Packed Size & Weight",
  summary: "Use the actual packed information for the order when available rather than applying one generic figure to every machine.",
  icon: Scale,
  tag: "Specifications"
}, {
  step: "03",
  title: "Destination",
  summary: "Delivery options can depend on where the equipment is going.",
  icon: MapPin,
  tag: "Transit route"
}, {
  step: "04",
  title: "Unloading Access",
  summary: "Confirm how the shipment can be received at the destination before dispatch.",
  icon: Warehouse,
  tag: "Receiving access"
}];
export default function Freight() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: "easeOut"
      }
    }
  };
  return <section id="shipping-delivery" data-nav="light" className="relative bg-background text-foreground border-b border-border py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        {/* Section Header */}
        <div className="mb-12 border-b border-border pb-8 md:mb-16">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-tight text-foreground">
                How Is a Mechanical Bull Shipped?
              </h2>
              <p className="mt-3 font-sans text-base text-muted-foreground leading-relaxed">
                Mechanical bulls generally require planned equipment delivery rather than ordinary parcel shipping. The exact method depends on the bull, motion base, controller, inflatable, blower, accessories, packing arrangement and destination.
              </p>
            </div>
            <div className="max-w-md">
              <p className="font-sans text-sm sm:text-base text-muted-foreground leading-relaxed">
                Because machines can differ substantially in size and configuration, the delivery method should be confirmed for the individual order instead of assuming every mechanical bull ships the same way.
              </p>
            </div>
          </div>
        </div>

        {/* Two-Column Grid */}
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
          {/* Left Column: Equipment Imagery & Inquiry Info */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            <div className="border border-border bg-card p-2">
              <div className="relative overflow-hidden bg-muted border border-border">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799866/img/commercial_freight_crate.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799866/img/commercial_freight_crate.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799866/img/commercial_freight_crate.png-wvc-sizes" alt="Mechanical bull equipment prepared for freight delivery" loading="lazy" decoding="async" className="w-full object-cover aspect-4/3" />
              </div>

              {/* Verified Order Note */}
              <div className="p-4 sm:p-5 mt-2 border border-border bg-muted/40">
                <div className="flex items-center justify-between border-b border-border pb-3">
                  <span className="font-mono text-xs font-semibold uppercase tracking-wider text-foreground">
                    Order Confirmation
                  </span>
                  <span className="font-mono text-xs text-muted-foreground uppercase">
                    Delivery Planning
                  </span>
                </div>
                <p className="mt-3 font-sans text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  Every order configuration is reviewed individually. Confirm your specific delivery details, destination requirements, and access capabilities directly with our team prior to dispatch.
                </p>
              </div>
            </div>

            {/* Quick Contact Box */}
            <div className="border border-border bg-card p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <span className="font-mono text-xs text-muted-foreground uppercase tracking-wider block">
                  Logistics Coordination
                </span>
                <span className="font-sans text-sm font-semibold text-foreground">
                  Confirm delivery options for your order
                </span>
              </div>
              <Button variant="outline" size="sm" asChild className="font-mono text-xs tracking-wider uppercase border-border rounded-lg">
                <Link to="/ContactPage">
                  <PhoneCall className="mr-1.5 size-3.5" />
                  Contact team
                </Link>
              </Button>
            </div>
          </div>

          {/* Right Column: Four Considerations Cards */}
          <div className="lg:col-span-7 flex flex-col justify-between">
            <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
            once: true,
            amount: 0.2
          }} className="divide-y divide-border border-y border-border">
              {factors.map((item, index) => {
              const IconComponent = item.icon;
              return <motion.div key={item.step} data-index={index} variants={itemVariants} className="group py-6 md:py-8 first:pt-4 last:pb-4 transition-[background-color] duration-150 hover:bg-muted/30">
                    <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-6">
                      {/* Step Indicator and Icon */}
                      <div className="flex items-center gap-3 sm:flex-col sm:items-start shrink-0">
                        <span className="font-mono text-sm font-bold text-primary tracking-wider">
                          [{item.step}]
                        </span>
                        <div className="size-10 border border-border bg-card flex items-center justify-center text-foreground group-hover:border-foreground transition-[border-color] duration-150">
                          <IconComponent className="size-5" />
                        </div>
                      </div>

                      {/* Content */}
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                          <h3 className="font-serif text-xl sm:text-2xl font-bold uppercase tracking-wide text-foreground">
                            {item.title}
                          </h3>
                          <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground border border-border px-2 py-0.5 bg-muted">
                            {item.tag}
                          </span>
                        </div>
                        <p className="font-sans text-sm sm:text-base text-muted-foreground leading-relaxed">
                          {item.summary}
                        </p>
                      </div>
                    </div>
                  </motion.div>;
            })}
            </motion.div>

            {/* Bottom Actions and Guidelines */}
            <div className="mt-8 pt-6 border-t border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="font-mono text-xs text-muted-foreground">
                Questions on venue clearance or electrical access?
              </div>
              <div className="flex flex-wrap gap-3">
                <Button variant="outline" asChild className="font-serif uppercase tracking-wider text-sm font-semibold rounded-lg border-foreground hover:bg-foreground hover:text-background">
                  <Link to="/SetupSafetyAndMaintenancePage">
                    Venue setup guide
                    <ArrowUpRight className="ml-1 size-4" />
                  </Link>
                </Button>
                <Button asChild className="bg-primary text-primary-foreground font-serif uppercase tracking-wider text-sm font-semibold rounded-lg hover:bg-primary/90">
                  <Link to="/ContactPage">Inquire about shipping</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}